<template>
  <div class="app container-fluid main-view">
    <section class="map-container">
      <div class="row">
        <div class="col-lg-9 col-md-8 px-0">
          <GmapMap id="map"
                   ref="devicesMap"
                   :zoom="mapZoom"
                   :center="mapCenter"
                   :options="mapConfig">
            <GmapMarker
              v-for="(marker, index) in markers"
              :ref="getMarkerKey(index)"
              :key="getMarkerKey(index)"
              :title="marker.name"
              :position="marker.position"
              :draggable="false"
              :zIndex="-20"
              :icon="markerIcon"
              @click="openMarkerWindow(index)">
              <GmapInfoWindow
                :options="infoWinConfig"
                :position="marker.position"
                :opened="index === openMarkerId"
                @closeclick="openMarkerId = null">
                <div class="infowindow_content">
                  <p class="title">{{ marker.name }}</p>
                  <p class="address mb-3">{{ marker.address }}</p>
                  <ul class="working-hours list-unstyled">
                    <li class="working-hour mb-1" v-for="workingHour in marker.working_hours">
                      <div class="row">
                        <div class="col-2">
                          <strong>{{ workingHour.day_of_week_text }}</strong>
                        </div>
                        <div class="col-10">
                          <span>{{ workingHour.working_hours_from ? workingHour.working_hours_from.substring(0, 5) : '' }}</span> - <span>{{ workingHour.working_hours_to ? workingHour.working_hours_to.substring(0, 5) : ''}}</span>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </GmapInfoWindow>
            </GmapMarker>
          </GmapMap>
        </div>
        <div class="col-lg-3 col-md-4 place-browser">
          <div class="places-container">
            <div class="mb-4" id="placeList">
              <div v-if="spinners.contentIsLoading" class="text-center px-4 py-3">
                <b-spinner type="grow" variant="primary"/>
              </div>
              <div v-else v-for="(marker, index) in markers"
                   class="item"
                   :key="'item' + index"
                   :class="openMarkerId === index ? 'active' : ''"
                   @click="openMarkerWindow(index)">
                <div>{{ marker.name }}</div>
                <p class="address">{{ marker.address }}</p>
                <ul v-if="openMarkerId === index" class="working-hours-side list-unstyled">
                  <li class="working-hour" v-for="workingHour in marker.working_hours">
                    <div class="row">
                      <div class="col-2">
                        <small><strong>{{ workingHour.day_of_week_text }}</strong></small>
                      </div>
                      <div class="col-10">
                        <small><span>{{ workingHour.working_hours_from ? workingHour.working_hours_from.substring(0, 5) : '' }}</span> - <span>{{ workingHour.working_hours_to ? workingHour.working_hours_to.substring(0, 5) : '' }}</span></small>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <a href="#" class="btn px-4 py-3 btn-danger" :class="locationLoaded ? '' : 'disabled'" @click="findClosestDevice">{{ $t('find_nearest_device') }}</a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import googleMaps from '@/_google-maps';
import { mapGetters, mapActions } from 'vuex';
import VuexTypes from '@/store/types/index';
import forEach from 'lodash.foreach';
import statuses from '@/store/statuses';
import Vue from 'vue';

const DevicesTypes = VuexTypes.devices;

export default {
  components: {},
  props: [],
  data () {
    return {
      spinners: {
        contentIsLoading: false
      },
      markers: [],
      mapZoom: 13,
      mapCenter: {
        lat: 56.9496,
        lng: 24.1052
      },
      mapObject: null,
      openMarkerId: null,
      markerIcon: null,
      markerIconImgPath: require('@/assets/img/marker-blue.svg'),
      infoWinConfig: {
        maxWidth: 400
      },
      selectedMarker: null,
      mapConfig: googleMaps.mapConfig,
      locationLoaded: false
    }
  },
  mounted () {
    // First load the map as a promise and then work with it
    this.$refs.devicesMap.$mapPromise.then((map) => {
      this.mapObject = map;
      this.getDevices();
      this.mapObject.panTo(this.mapCenter);

      // Icon is the same for all markers
      this.markerIcon = {
        url: this.markerIconImgPath,
        anchor: new google.maps.Point(25, 64),
        origin: new google.maps.Point(0,0),
        scaledSize: new google.maps.Size(50, 64)
      };
    });
  },
  computed: {
    ...mapGetters('Devices', [
      'devicesListByLocation'
    ])
  },
  watch: {
    devicesListByLocation: {
      handler: function () {
        if (this.devicesListByLocation.status === statuses.LOADED) {
          this.fillMarkers();
          this.spinners.contentIsLoading = false;
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('Devices', [
      DevicesTypes.DEVICES_LIST_BY_LOCATION
    ]),
    getMarkerKey(index) {
      return 'device-marker-' + index;
    },
    openMarkerWindow(index) {
      const refName = this.getMarkerKey(index);
      const markerObject = this.$refs[refName][0].$markerObject;
      this.mapObject.panTo(markerObject.getPosition());
      this.mapObject.setZoom(15);
      this.openMarkerId = index;
    },
    fillMarkers() {
      const markerList = [];
      forEach(this.devicesListByLocation.data.data, (item) => {
        markerList.push({
          name: item.name,
          address: item.address,
          working_hours: item.device_working_hours,
          position: {
            lat: parseFloat(item.latitude),
            lng: parseFloat(item.longitude)
          }
        });
      });

      this.markers = markerList;
    },
    getDevices() {
      // Try to get the location of the device
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.mapCenter = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          Vue.set(this, 'locationLoaded', true);
        });
      } else {
        Vue.set(this, 'locationLoaded', false);
      }

      this.spinners.contentIsLoading = true;
      this.DEVICES_LIST_BY_LOCATION({
        lat: this.mapCenter.lat,
        long: this.mapCenter.lng
      });
    },
    findClosestDevice() {
      this.getDevices();

      this.openMarkerId = this.markers.length > 0 ? 0 : null; // First one is the closest one
      this.mapObject.setZoom(this.mapZoom);
      if (this.openMarkerId !== null) {
        this.mapObject.panTo(this.markers[this.openMarkerId].position);
      }
    }
  }
}
</script>

<style scoped>
</style>
